import React, { useEffect, useState } from "react";
import { Button, Table, Select, Group, NumberInput, ActionIcon, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconTrash, IconEdit, IconCheck, IconX, IconTags } from "@tabler/icons-react";
import CreateOfferModal from "./CreateOfferModal";
import { offersServices } from "../../services/offersServices";
import useAuthStore from "../../store/useAuthStore";

const Offers = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const isAdmin = useAuthStore((state) => state.isAdmin);

    const [offers, setOffers] = useState([]);
    const [filteredOffers, setFilteredOffers] = useState([]);

    const [partnerships, setPartnerships] = useState([]);
    const [verticals, setVerticals] = useState([]);

    const [selectedPartnership, setSelectedPartnership] = useState(null);
    const [selectedVertical, setSelectedVertical] = useState(null);

    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});

    const fetchOffers = async () => {
        try {
            const data = await offersServices.getAllOffers();
            setOffers(data);
            setFilteredOffers(data);

            const uniquePartnerships = Array.from(
                new Set(data.map((o) => o.partnership?.name))
            ).filter(Boolean);
            const uniqueVerticals = Array.from(
                new Set(data.map((o) => o.vertical?.name))
            ).filter(Boolean);

            setPartnerships(uniquePartnerships);
            setVerticals(uniqueVerticals);
        } catch (error) {
            console.error("Error fetching offers:", error);
        }
    };

    useEffect(() => {
        fetchOffers();
    }, []);

    useEffect(() => {
        let filtered = offers;

        if (selectedPartnership) {
            filtered = filtered.filter(
                (offer) => offer.partnership?.name === selectedPartnership
            );
        }

        if (selectedVertical) {
            filtered = filtered.filter(
                (offer) => offer.vertical?.name === selectedVertical
            );
        }

        setFilteredOffers(filtered);
    }, [selectedPartnership, selectedVertical, offers]);

    const startEditing = (offer) => {
        setEditingId(offer.id);
        setEditValues({ ...offer });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditValues({});
    };

    const saveEditing = async () => {
        try {
            const updatedOffer = await offersServices.updateOffer({
                id: editingId,
                ...editValues
            });

            const newData = offers.map(item => item.id === editingId ? updatedOffer : item);
            setOffers(newData);
            setEditingId(null);
            setEditValues({});
        } catch (err) {
            console.error("Save failed:", err);
        }
    };

    const handleDelete = (id) => {
        modals.openConfirmModal({
            title: (
                <Group gap="xs">
                    <IconTags size={18} color="red" />
                    <Text fw={700}>Delete Offer</Text>
                </Group>
            ),
            centered: true,
            children: (
                <Text size="sm">
                    Are you sure you want to delete this offer? This action is irreversible.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red', variant: 'filled' },
            onConfirm: async () => {
                try {
                    await offersServices.deleteOffer(id);
                    const updatedOffers = offers.filter((o) => o.id !== id);
                    setOffers(updatedOffers);
                    notifications.show({
                        title: 'Success',
                        message: 'Offer deleted successfully',
                        color: 'green'
                    });
                } catch (error) {
                    console.error("Error deleting offer:", error);
                    notifications.show({
                        title: 'Error',
                        message: 'Failed to delete offer',
                        color: 'red'
                    });
                }
            },
        });
    };

    const rows = filteredOffers.map((offer) => {
        const isEditing = editingId === offer.id;

        return (
            <Table.Tr key={offer.id}>
                <Table.Td>{offer.id}</Table.Td>
                <Table.Td>{offer.geo}</Table.Td>
                <Table.Td>{offer.name}</Table.Td>
                <Table.Td>{offer.vertical?.name}</Table.Td>
                <Table.Td>{offer.partnership?.name}</Table.Td>
                <Table.Td>
                    {isEditing ? (
                        <NumberInput
                            value={editValues.payout}
                            onChange={(val) => setEditValues({ ...editValues, payout: val })}
                            min={0}
                            size="xs"
                        />
                    ) : (
                        `$${parseFloat(offer.payout).toFixed(2)}`
                    )}
                </Table.Td>
                <Table.Td>
                    {isEditing ? (
                        <NumberInput
                            value={editValues.approvalRate}
                            onChange={(val) => setEditValues({ ...editValues, approvalRate: val })}
                            min={0}
                            max={100}
                            size="xs"
                        />
                    ) : (
                        `${parseFloat(offer.approvalRate).toFixed(0)}%`
                    )}
                </Table.Td>
                <Table.Td>${parseFloat(offer.maxLeadPrice).toFixed(2)}</Table.Td>
                <Table.Td>
                    {isAdmin && (
                        <Group gap="xs">
                            {isEditing ? (
                                <>
                                    <ActionIcon color="green" onClick={saveEditing}>
                                        <IconCheck size={16} />
                                    </ActionIcon>
                                    <ActionIcon color="red" onClick={cancelEditing}>
                                        <IconX size={16} />
                                    </ActionIcon>
                                </>
                            ) : (
                                <>
                                    <ActionIcon variant="subtle" color="blue" onClick={() => startEditing(offer)}>
                                        <IconEdit size={16} />
                                    </ActionIcon>
                                    <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(offer.id)}>
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </>
                            )}
                        </Group>
                    )}
                </Table.Td>
            </Table.Tr>
        );
    });

    return (
        <div>
      <div className="workspace-heading"><div><h2>Offers</h2><p>Payouts, partners and verticals across your offer portfolio.</p></div></div>
            {isAdmin && (
                <Button mb="md" onClick={() => setIsModalVisible(true)}>
                    Create Offer
                </Button>
            )}

            <Group mb="md">
                <Select
                    placeholder="Filter by Partnership"
                    data={partnerships}
                    value={selectedPartnership}
                    onChange={setSelectedPartnership}
                    clearable
                    searchable
                />
                <Select
                    placeholder="Filter by Vertical"
                    data={verticals}
                    value={selectedVertical}
                    onChange={setSelectedVertical}
                    clearable
                    searchable
                />
            </Group>

            <Table.ScrollContainer minWidth={800}>
                <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>ID</Table.Th>
                            <Table.Th>Geo</Table.Th>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Vertical</Table.Th>
                            <Table.Th>Partnership</Table.Th>
                            <Table.Th>Payout</Table.Th>
                            <Table.Th>Approval Rate</Table.Th>
                            <Table.Th>Max Lead Price</Table.Th>
                            <Table.Th>Actions</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{rows}</Table.Tbody>
                </Table>
            </Table.ScrollContainer>

            <CreateOfferModal
                isVisible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onSuccess={() => {
                    setIsModalVisible(false);
                    fetchOffers();
                }}
            />
        </div>
    );
};

export default Offers;
